/**
 * `convert/unit` — five-dimension unit converter.
 *
 * Five physical dimensions, each with a hand-curated unit table.
 * Numeric conversions go through a per-dimension SI base unit so we
 * never compose two arbitrary conversion factors with floating-point
 * rounding (the round-trip ratio for `lb → oz → g → kg → lb` is the
 * same as `lb → kg → lb`).
 *
 * The temperature dimension is intentionally non-linear: Kelvin /
 * Celsius / Fahrenheit need offset terms (you cannot scale them
 * with a single `factor`). Those three units use a small custom
 * `to/from` pair against Celsius (the chosen base for that
 * dimension).
 *
 * Units we deliberately skip:
 *   - Imperial gallons / fl-oz: trivial extension if we get a real
 *     ask. Skipped to keep the table at a glanceable size.
 *   - Astronomical units / parsecs: not in scope for a "developer
 *     unit converter" — would distract from the daily-driver units.
 */

export type Dimension = 'length' | 'mass' | 'temp' | 'data' | 'time';

export const DIMENSIONS: ReadonlyArray<Dimension> = [
  'length',
  'mass',
  'temp',
  'data',
  'time',
];

export const DIMENSION_LABEL: Record<Dimension, string> = {
  length: '长度',
  mass: '重量',
  temp: '温度',
  data: '数据',
  time: '时间',
};

interface LinearUnit {
  id: string;
  label: string;
  /** Multiplier from this unit to the dimension's base unit. */
  factor: number;
}

interface NonlinearUnit {
  id: string;
  label: string;
  toBase: (n: number) => number;
  fromBase: (n: number) => number;
}

type Unit = LinearUnit | NonlinearUnit;

interface DimensionTable {
  base: string; // base unit id
  units: ReadonlyArray<Unit>;
}

/* ------------------------------------------------------------------ */
/* Tables                                                              */
/* ------------------------------------------------------------------ */

// length: base = m
const LENGTH: DimensionTable = {
  base: 'm',
  units: [
    { id: 'mm', label: '毫米 (mm)', factor: 1e-3 },
    { id: 'cm', label: '厘米 (cm)', factor: 1e-2 },
    { id: 'm', label: '米 (m)', factor: 1 },
    { id: 'km', label: '千米 (km)', factor: 1e3 },
    { id: 'in', label: '英寸 (in)', factor: 0.0254 },
    { id: 'ft', label: '英尺 (ft)', factor: 0.3048 },
    { id: 'yd', label: '码 (yd)', factor: 0.9144 },
    { id: 'mi', label: '英里 (mi)', factor: 1609.344 },
    { id: 'nmi', label: '海里 (nmi)', factor: 1852 },
  ],
};

// mass: base = kg
const MASS: DimensionTable = {
  base: 'kg',
  units: [
    { id: 'mg', label: '毫克 (mg)', factor: 1e-6 },
    { id: 'g', label: '克 (g)', factor: 1e-3 },
    { id: 'kg', label: '千克 (kg)', factor: 1 },
    { id: 't', label: '吨 (t)', factor: 1e3 },
    { id: 'oz', label: '盎司 (oz)', factor: 0.028349523125 },
    { id: 'lb', label: '磅 (lb)', factor: 0.45359237 },
  ],
};

// temp: base = °C
const TEMP: DimensionTable = {
  base: 'C',
  units: [
    {
      id: 'C',
      label: '摄氏度 (°C)',
      toBase: (c) => c,
      fromBase: (c) => c,
    },
    {
      id: 'F',
      label: '华氏度 (°F)',
      toBase: (f) => (f - 32) * (5 / 9),
      fromBase: (c) => c * (9 / 5) + 32,
    },
    {
      id: 'K',
      label: '开尔文 (K)',
      toBase: (k) => k - 273.15,
      fromBase: (c) => c + 273.15,
    },
  ],
};

// data: base = byte. Includes both binary (KiB) and decimal (KB).
const DATA: DimensionTable = {
  base: 'B',
  units: [
    { id: 'b', label: '比特 (bit)', factor: 1 / 8 },
    { id: 'B', label: '字节 (B)', factor: 1 },
    { id: 'KiB', label: 'KiB (1024 B)', factor: 1024 },
    { id: 'MiB', label: 'MiB', factor: 1024 ** 2 },
    { id: 'GiB', label: 'GiB', factor: 1024 ** 3 },
    { id: 'TiB', label: 'TiB', factor: 1024 ** 4 },
    { id: 'KB', label: 'KB (1000 B)', factor: 1000 },
    { id: 'MB', label: 'MB', factor: 1000 ** 2 },
    { id: 'GB', label: 'GB', factor: 1000 ** 3 },
    { id: 'TB', label: 'TB', factor: 1000 ** 4 },
  ],
};

// time: base = s
const TIME: DimensionTable = {
  base: 's',
  units: [
    { id: 'ns', label: '纳秒 (ns)', factor: 1e-9 },
    { id: 'us', label: '微秒 (μs)', factor: 1e-6 },
    { id: 'ms', label: '毫秒 (ms)', factor: 1e-3 },
    { id: 's', label: '秒 (s)', factor: 1 },
    { id: 'min', label: '分钟 (min)', factor: 60 },
    { id: 'h', label: '小时 (h)', factor: 3600 },
    { id: 'd', label: '天 (d)', factor: 86400 },
    { id: 'wk', label: '周 (wk)', factor: 86400 * 7 },
    // Year = Julian year (365.25 d), the convention astronomy uses.
    // Week ≠ year/52; we don't support custom calendar arithmetic here.
    { id: 'yr', label: '年 (Julian, 365.25 d)', factor: 86400 * 365.25 },
  ],
};

const TABLES: Record<Dimension, DimensionTable> = {
  length: LENGTH,
  mass: MASS,
  temp: TEMP,
  data: DATA,
  time: TIME,
};

/* ------------------------------------------------------------------ */
/* Lookup helpers                                                      */
/* ------------------------------------------------------------------ */

export function listUnits(d: Dimension): Array<{ id: string; label: string }> {
  return TABLES[d].units.map((u) => ({ id: u.id, label: u.label }));
}

function findUnit(d: Dimension, id: string): Unit {
  const u = TABLES[d].units.find((u) => u.id === id);
  if (!u) throw new Error(`unknown unit ${id} for dimension ${d}`);
  return u;
}

function toBase(d: Dimension, id: string, value: number): number {
  const u = findUnit(d, id);
  return 'factor' in u ? value * u.factor : u.toBase(value);
}

function fromBase(d: Dimension, id: string, base: number): number {
  const u = findUnit(d, id);
  return 'factor' in u ? base / u.factor : u.fromBase(base);
}

/* ------------------------------------------------------------------ */
/* Public API                                                          */
/* ------------------------------------------------------------------ */

/**
 * Convert `value` from `fromUnit` to `toUnit`, both in dimension `d`.
 *
 * NaN / Infinity inputs propagate verbatim (we don't pretend to
 * "fix" them — that would mask user input bugs).
 */
export function convertUnit(
  d: Dimension,
  fromUnit: string,
  toUnit: string,
  value: number,
): number {
  if (!Number.isFinite(value)) return value;
  const base = toBase(d, fromUnit, value);
  return fromBase(d, toUnit, base);
}

/**
 * Convenience: convert one input value to ALL units in the same
 * dimension. Useful for the page UI's "input one value, see every
 * unit" mode.
 */
export function convertToAllUnits(
  d: Dimension,
  fromUnit: string,
  value: number,
): Array<{ id: string; label: string; value: number }> {
  if (!Number.isFinite(value)) {
    return TABLES[d].units.map((u) => ({
      id: u.id,
      label: u.label,
      value,
    }));
  }
  const base = toBase(d, fromUnit, value);
  return TABLES[d].units.map((u) => ({
    id: u.id,
    label: u.label,
    value: 'factor' in u ? base / u.factor : u.fromBase(base),
  }));
}
